import { useEffect, useState } from "react";
import {
  Chip,
  Container,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material";
import { map, orderBy } from "lodash";
import MoedaText from "../../../util/ui/inputs/moedaText";
import { IconRefresh, IconX, IconCheck, IconClock } from "@tabler/icons-react";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { useAssinatura } from "../../../../providers/assinaturaProvider";
import { onSnapshot } from "firebase/firestore";
import FaturaDb from "../../../../db/faturaDb";
import Fatura, { SituacaoFatura } from "../../../../models/fatura";
import { radius } from "../../../../theme";

/** Chip de status da fatura: uma das cinco cores semânticas do sistema. */
type StatusFatura = "positive" | "accent" | "warn" | "destructive" | "neutral";

const chipSx = (status: StatusFatura) => (theme: Theme) => ({
  backgroundColor: theme.palette.statusChip[status].bg,
  color: theme.palette.statusChip[status].fg,
  "& .MuiChip-icon": { color: "inherit" },
});

export default function AbaFaturas() {
  const { usuario: { id } = { id: undefined } } = useUsuario();
  const assinatura = useAssinatura();
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  useEffect(() => {
    if (!!id && !!assinatura)
      return onSnapshot(
        new FaturaDb().collection({
          usuarioId: id,
          assinaturaId: assinatura.id,
          faturaId: "",
        }),
        (faturasSnapshot) => {
          if (!faturasSnapshot.empty) {
            const faturasDocs = map(faturasSnapshot.docs, (faturaDoc) =>
              faturaDoc.data()
            );
            setFaturas(faturasDocs);
          }
        }
      );
  }, [id, assinatura]);

  const getSituacaoTag = (situacao: SituacaoFatura) => {
    switch (situacao) {
      case SituacaoFatura.DRAFT:
        return (
          <Chip
            size="small"
            icon={<IconRefresh size={13} />}
            sx={chipSx("neutral")}
            label="CRIADA"
          ></Chip>
        );
      case SituacaoFatura.OPEN:
        return (
          <Chip
            size="small"
            icon={<IconClock size={13} />}
            sx={chipSx("warn")}
            label="AGUARDANDO"
          ></Chip>
        );
      case SituacaoFatura.PAID:
        return (
          <Chip
            size="small"
            icon={<IconCheck size={13} />}
            sx={chipSx("positive")}
            label="PAGA"
          ></Chip>
        );
      case SituacaoFatura.UNCOLLECTIBLE:
      case SituacaoFatura.VOID:
      default:
        return (
          <Chip
            size="small"
            icon={<IconX size={13} />}
            sx={chipSx("destructive")}
            label="ATRASADA"
          ></Chip>
        );
    }
  };

  return (
    <Container disableGutters>
      <TableContainer
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: `${radius.xl}px`,
          backgroundColor: "background.paper",
          overflowX: "auto",
        }}
      >
        <Table size="small" padding="normal">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Período</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Fatura</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(
              orderBy(faturas, (fatura) => fatura.criadoEm),
              (fatura, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="dataMono" color="text.secondary">
                        {fatura.criadoEm.format("DD/MM/YYYY")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {fatura.descricao}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="dataMono"
                        fontSize={12}
                        color="text.secondary"
                        whiteSpace="nowrap"
                      >
                        {fatura.periodoInicio.format("DD/MM/YYYY") +
                          " - " +
                          fatura.periodoFim.format("DD/MM/YYYY")}
                      </Typography>
                    </TableCell>
                    <TableCell>{getSituacaoTag(fatura.situacao)}</TableCell>
                    <TableCell>
                      <Link
                        href={fatura.pdf}
                        variant="labelMono"
                        color="accentInk"
                        underline="hover"
                      >
                        PDF
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="dataMono"
                        fontWeight={500}
                        color="text.primary"
                        whiteSpace="nowrap"
                      >
                        <MoedaText value={fatura.total} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
